import React from 'react';
import './DepartmentIdentityCell.css';

/**
 * Resolve LGD district fields from department / list row payloads.
 * See docs/FE_DEPARTMENT_DISTRICT.md
 */
export const getDepartmentDistrictFields = (row = {}) => {
    const nested =
        row?.department_details ||
        row?.department ||
        row?.dept ||
        {};
    const name =
        row?.district_name ??
        row?.department_district_name ??
        row?.departmentDistrictName ??
        row?.lgd_district_name ??
        nested?.district_name ??
        nested?.district ??
        null;
    const lgdCode =
        row?.district_lgd_code ??
        row?.department_district_lgd_code ??
        row?.departmentDistrictLgdCode ??
        row?.lgd_district_code ??
        row?.lgd_code ??
        nested?.district_lgd_code ??
        nested?.lgd_code ??
        null;
    const districtId =
        row?.district_id ??
        row?.department_district_id ??
        row?.departmentDistrictId ??
        nested?.district_id ??
        null;

    return {
        districtId,
        districtName: name == null || name === '' ? null : String(name),
        districtLgdCode:
            lgdCode == null || lgdCode === '' ? null : String(lgdCode),
    };
};

/**
 * When list APIs omit district on the row, fill from a known department
 * payload (e.g. logged-in user department_details) for own-department views.
 */
export const withDepartmentDistrictFallback = (row, fallbackSource) => {
    if (!row) return row;
    const existing = getDepartmentDistrictFields(row);
    if (existing.districtName) return row;
    const fallback = getDepartmentDistrictFields(fallbackSource || {});
    if (!fallback.districtName) return row;
    return {
        ...row,
        district_id: row.district_id ?? fallback.districtId ?? '',
        district_name: fallback.districtName,
        district_lgd_code: fallback.districtLgdCode ?? '',
    };
};

export const formatDepartmentDistrict = (row) => {
    const { districtName } = getDepartmentDistrictFields(row);
    return districtName || '';
};

/**
 * Shared district badge — district name only (no LGD code in UI).
 */
export const DepartmentDistrictBadge = ({
    row,
    districtName: districtNameProp,
    className = '',
}) => {
    const fromRow = row ? getDepartmentDistrictFields(row) : {};
    const districtName =
        districtNameProp ?? fromRow.districtName ?? null;

    if (!districtName) return null;

    return (
        <span
            className={`dept-district-badge${className ? ` ${className}` : ''}`}
        >
            {districtName}
        </span>
    );
};

/**
 * Standard department identity cell used on PPA / holding / similar lists.
 * Shows name, nomenclature, tier level, and district badge when present.
 *
 * @param {object} [fallbackDistrictRow] - e.g. auth user department_details
 *   when the list API omits district_* on each row
 */
const DepartmentIdentityCell = ({
    row,
    fallbackDistrictRow = null,
    showNomenclatureLabel = true,
    compact = false,
}) => {
    const enriched = withDepartmentDistrictFallback(row, fallbackDistrictRow);
    const displayName =
        enriched?.name || enriched?.department_name || '-';
    const nomenclature =
        enriched?.nomiclature || enriched?.department_nomiclature || '';
    const level = enriched?.department_level || enriched?.tier || '';
    const { districtName } = getDepartmentDistrictFields(enriched);

    return (
        <div style={{ lineHeight: 1.2 }}>
            <div style={{ color: '#6b7280', marginTop: compact ? 0 : 4 }}>
                {displayName}
            </div>
            {nomenclature ? (
                <div style={{ color: '#6b7280', marginTop: 4 }}>
                    {showNomenclatureLabel
                        ? `Nomenclature: ${nomenclature}`
                        : nomenclature}
                </div>
            ) : null}
            {level ? (
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                    Level: {level}
                </div>
            ) : null}
            {districtName ? (
                <div style={{ marginTop: 6 }}>
                    <DepartmentDistrictBadge row={enriched} />
                </div>
            ) : null}
        </div>
    );
};

export default DepartmentIdentityCell;
